/**
 * One player, several teams, one ladder.
 *
 * A ladder with `allowMultipleTeamsPerPlayer` lets Anna pair with Ben on one
 * team and with Cara on another, both competing in the same doubles ladder.
 * Two things follow that the app has to answer consistently:
 *
 *   - **Anna+Ben versus Anna+Cara is unplayable.** Anna cannot be on both
 *     sides, so the two teams may never challenge each other. The API decides
 *     this (it greys the pill via `canChallenge` and refuses the write), but the
 *     rule lives here too so the "challenging as" picker can filter its own
 *     options without a round trip.
 *
 *   - **"Which row is me" stops having one answer.** `myTeamFromListOfTeams`
 *     is a `.find()`, so it quietly returned whichever of Anna's rows came
 *     first. `myLadderTeams` returns all of them, best ranked first.
 *
 * Identity note: this side keys membership on PLAYER id, matching the existing
 * `isPlayerInTeam` in `useValidation`. The Flutter app keys on user id. Both
 * describe the same person; each keeps the id its own payloads carry.
 *
 * Mirrored by the API's `LadderMultiTeamExtensions` and the Flutter app's
 * `ladder_multi_team.dart` — change all three together.
 *
 * Imported explicitly (house style), not relied on via auto-import.
 */
export function useLadderMultiTeam() {
  /**
   * Shown when a challenge is refused because both teams contain the same
   * player. Identical to the API's `SharedPlayerBlockedReason` so a
   * locally-blocked option and a server-blocked one read the same.
   */
  const sharedPlayerBlockedReason = 'These teams share a player';

  /**
   * Whether the same player appears in both line-ups, which is what makes a
   * fixture between them unplayable.
   *
   * Fails OPEN on an unknown line-up — an empty or null side shares nothing. A
   * team whose players have not loaded is a missing answer, not a clash, and
   * treating it as one would grey out the whole challenge grid.
   */
  const teamsSharePlayer = (aPlayerIds, bPlayerIds) => {
    if (!aPlayerIds || !bPlayerIds) return false;

    const b = new Set(bPlayerIds);
    if (b.size === 0) return false;

    return [...aPlayerIds].some(id => b.has(id));
  };

  /** `teamsSharePlayer` for two standings rows, reading each line-up off the team. */
  const ladderTeamsSharePlayer = (a, b) => teamsSharePlayer(
      a?.players?.map(p => p?.id),
      b?.players?.map(p => p?.id),
  );

  /**
   * Every row in the ladder the given player is part of, best ranked first.
   *
   * Replaces `myTeamFromListOfTeams` for ladder standings. That helper is still
   * right where there genuinely is one team — the two sides of a fixture — but
   * picking one row out of the standings is not, once there can be several.
   */
  const myLadderTeams = (teams, playerId) => {
    if (!teams || !playerId) return [];

    return teams
        .filter(t => isMyLadderTeam(t, playerId))
        .sort((a, b) => (a?.newPosition ?? UNRANKED) - (b?.newPosition ?? UNRANKED));
  };

  /**
   * Whether this particular row belongs to the given player.
   *
   * Safe under multi-team and deliberately so: BOTH of Anna's rows are hers,
   * and both should highlight in the standings.
   */
  const isMyLadderTeam = (team, playerId) =>
      !!playerId && (team?.players ?? []).some(p => p?.id === playerId);

  /**
   * The players held by more than one of the given line-ups.
   *
   * A player repeated WITHIN one line-up is not a cross-team clash.
   */
  const playersInMoreThanOneTeam = (lineUps) => {
    if (!lineUps) return [];

    const seen = new Set();
    const shared = [];

    for (const lineUp of lineUps) {
      if (!lineUp) continue;
      for (const playerId of new Set(lineUp)) {
        if (seen.has(playerId)) {
          if (!shared.includes(playerId)) shared.push(playerId);
        } else {
          seen.add(playerId);
        }
      }
    }

    return shared;
  };

  /**
   * Whether any two line-ups name the identical set of players.
   *
   * Different partners are the point of the feature; the SAME pairing twice is
   * not supported. Team identity is the set of its players, so two identical
   * line-ups resolve to one team on the API and the second silently overwrites
   * the first's ladder position.
   */
  const hasDuplicateLineUp = (lineUps) => {
    if (!lineUps) return false;

    const seen = new Set();

    for (const lineUp of lineUps) {
      if (!lineUp) continue;
      const key = lineUpKey(lineUp);
      if (key === '') continue;
      if (seen.has(key)) return true;
      seen.add(key);
    }

    return false;
  };

  /**
   * Whether a dragged player may be dropped where the cursor currently is.
   *
   * A player may be in several TEAMS but never twice in the SAME team: removing
   * them would strip both copies, and adding them back would insert two.
   *
   * Both draggables ask this — the pool and the team list — because the guard
   * that runs belongs to the list the drag STARTED in. Guarding only the team
   * list left dragging from the pool unchecked.
   *
   * The duplicate check runs ONLY when dropping into a team's player list. A
   * singles team's id is its player's id (TeamService reuses it), so testing it
   * against the TEAMS list would compare a team id to a player id and refuse a
   * legitimate new team.
   */
  const canDropPlayerIntoTeam = (
      {intoTeamList, intoPlayerList, intoPool, draggedPlayerId, targetPlayers, maxPlayers}) => {
    // Back out to a pool (available players, or the waiting area). Whether a given
    // pool takes drops at all is its own `put`; nothing about the player forbids it.
    if (intoPool) return true;
    if (intoPlayerList) {
      const players = targetPlayers ?? [];
      // The same person twice in one team is corrupting: removing them strips
      // both copies and adding them back inserts two.
      if (draggedPlayerId && players.some(p => p?.id === draggedPlayerId)) return false;
      if (players.length < maxPlayers) return true;

      // A FULL team still accepts the drop, but only where a team is a group of
      // people: parking someone in a full team is how you swap two over, then
      // drag the other one out. On a SINGLES ladder every row is one player and
      // there is nothing to swap into — allowing it there just builds a
      // two-player team the ladder can't have and blocks saving.
      return maxPlayers > 1;
    }

    return !!intoTeamList;
  };

  /**
   * The teams with one player taken out of ONE named team.
   *
   * The team is identified, never searched for by player: looking the player up
   * finds whichever team comes first, so removing someone from their second
   * team gutted their first as soon as anyone played in two.
   *
   * A team left with nobody is dropped, matching what the API does with an
   * empty line-up. Order is preserved, and an unknown team id changes nothing.
   */
  const withoutPlayerFromTeam = (teams, teamId, playerId) => {
    if (!teams) return [];

    return teams
        .map((team) => {
          if (team?.id !== teamId) return team;
          return {...team, players: (team.players ?? []).filter(p => p?.id !== playerId)};
        })
        .filter(team => (team?.players ?? []).length > 0);
  };

  /**
   * The positions of every team whose line-up is identical to another's.
   *
   * Different partners are the point of the feature; the SAME pairing twice is
   * not supported, because team identity is the set of its players — two
   * identical line-ups resolve to one team on the API and the second silently
   * overwrites the first's ladder position.
   *
   * Reports BOTH offending teams rather than only the later one, so the admin
   * can change whichever they meant to.
   */
  const duplicateLineUpIndexes = (lineUps) => {
    if (!lineUps) return [];

    const byKey = new Map();
    [...lineUps].forEach((lineUp, index) => {
      if (!lineUp) return;
      const key = lineUpKey(lineUp);
      if (key === '') return;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(index);
    });

    return [...byKey.values()].filter(v => v.length > 1).flat().sort((a, b) => a - b);
  };

  /**
   * The players held by more than one of the given line-ups, as PLAYERS rather
   * than ids, so a warning can name them.
   *
   * Used when an admin turns multi-team OFF while someone is still in two
   * teams: the save would be refused server-side, and "a player is in more than
   * one team" is not enough to act on when you cannot see which.
   */
  const playersInMoreThanOneTeamAmong = (lineUps) => {
    if (!lineUps) return [];

    const teams = [...lineUps];
    const clashingIds = playersInMoreThanOneTeam(
        teams.map(lineUp => lineUp?.map(p => p?.id)));
    if (clashingIds.length === 0) return [];

    // First occurrence of each, so the order matches playersInMoreThanOneTeam.
    const byId = new Map();
    for (const lineUp of teams) {
      for (const player of lineUp ?? []) {
        if (player?.id != null && !byId.has(player.id)) byId.set(player.id, player);
      }
    }

    return clashingIds.map(id => byId.get(id)).filter(Boolean);
  };


  /**
   * Who the member manager may still offer, given who is already placed.
   *
   * On an ordinary ladder a placed player is spent. On a multi-team ladder they
   * stay in the pool so they can be paired again with someone else — which is
   * the entire feature. Adding the same person to the same team twice is
   * stopped separately, by the team's own line-up.
   */
  const availablePlayersForLadder = (clubPlayers, placedPlayerIds, allowMultipleTeamsPerPlayer) => {
    const players = clubPlayers ?? [];
    if (allowMultipleTeamsPerPlayer) return [...players];

    const placed = new Set(placedPlayerIds ?? []);
    return players.filter(p => !placed.has(p?.id));
  };

  /**
   * An order-independent, duplicate-independent identity for a line-up, so
   * "Anna, Ben" and "Ben, Anna" are recognised as the same team.
   */
  const lineUpKey = (playerIds) => [...new Set(playerIds)].sort().join(',');

  /** Sorts a team with no ladder position after every ranked one. */
  const UNRANKED = Number.MAX_SAFE_INTEGER;

  /**
   * Which of the viewer's teams may challenge a given standings row.
   *
   * The API sends `challengeableAs` only when the viewer holds more than one
   * team; with one it stays absent, so this is empty and the caller falls back
   * to that single team — the shape every existing ladder has.
   */
  const challengeOptionsFor = (team) =>
      (team?.challengeableAs ?? []).filter(o => o?.canChallenge);

  /**
   * The team to challenge AS, when the viewer hasn't picked one.
   *
   * Deliberately not "my first team": with several teams, the first may be the
   * one that CANNOT challenge this row (it shares a player with it, or it is
   * out of range), and challenging as it would be rejected. Prefer the first
   * team the API says is eligible.
   */
  const defaultChallengingTeamId = (team, myTeams) => {
    const options = challengeOptionsFor(team);
    return options.length > 0 ? options[0].teamId : (myTeams ?? [])[0]?.id;
  };

  /**
   * Why a row can't be challenged — one line per team of the viewer's when
   * their reasons differ.
   *
   * `challengeBlockedReason` is a single string, and where the viewer holds
   * several teams the API fills it from the BEST-RANKED one. That reads as a
   * statement about the viewer: someone holding position 1 and position 5 was
   * told "Cannot challenge teams ranked below you" about a row their position-5
   * team is simply out of range of. True of one team, wrong as an explanation.
   *
   * Returns [] when the row IS challengeable. One line when every team is
   * blocked for the same reason (naming them would add nothing), otherwise a
   * line per team.
   */
  const challengeBlockedLines = (team) => {
    if (!team || team.canChallenge) return [];

    const options = (team.challengeableAs ?? [])
        .filter(o => o && !o.canChallenge && o.blockedReason);

    // One team, or a payload without the breakdown: the single reason is
    // unambiguous, because there is only one team it could be about.
    if (options.length === 0) {
      return team.challengeBlockedReason ? [team.challengeBlockedReason] : [];
    }

    // The viewer's own row. "It's yours" is the whole answer; adding why their
    // OTHER team can't play it either is noise.
    const own = options.find(o => o.teamId === team.id);
    if (own) return [own.blockedReason];

    const distinct = [...new Set(options.map(o => o.blockedReason))];
    if (distinct.length === 1) return distinct;

    return options.map(o => `${o.name}: ${o.blockedReason}`);
  };

  return {
    sharedPlayerBlockedReason,
    teamsSharePlayer,
    ladderTeamsSharePlayer,
    myLadderTeams,
    isMyLadderTeam,
    playersInMoreThanOneTeam,
    hasDuplicateLineUp,
    availablePlayersForLadder,
    playersInMoreThanOneTeamAmong,
    duplicateLineUpIndexes,
    withoutPlayerFromTeam,
    canDropPlayerIntoTeam,
    challengeOptionsFor,
    defaultChallengingTeamId,
    challengeBlockedLines,
  };
}
